import { graphqlPost } from "api/graphqlClient";
import { getByExecutionTypeName } from "components/Icon/Icon";
import { FeExecutionType } from "config/config";
import RecordTable from "components/Table/RecordTable";
import { GroupWithMembers } from "components/Table/groupRows";
import { TableColumn } from "components/Table/tableUtils";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import { CustomObjectProvisioningState, ExecutionStatus } from "graphql/generated/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStatusMessageText } from "utils/utils";
import { DashboardRecordLink } from "../DashboardRecordLink";
import { DashboardLiveWidgetShell } from "../DashboardLiveWidgetShell";
import { describeWidgetFetchError } from "../dashboardWidgetError";
import { LatestNListWidgetConfig, WidgetComponentProps } from "../types";
import { useDashboardLiveRefresh } from "../useDashboardLiveRefresh";

interface LatestListRecord {
    id: string;
    name: string;
    created_at?: string;
    status?: ExecutionStatus;
    type?: string;
    message?: string;
    provisioning_state?: CustomObjectProvisioningState;
    [key: string]: unknown;
}

const POLL_INTERVAL_MS = 10000;
const DEFAULT_N_PER_GROUP = 1;
const DEFAULT_GROUP_LIMIT = 20;
const DEFAULT_ORDER = "-created_at";

const extraFieldsForType = (apiType: string, partitionField: string): string => {
    const fields = [partitionField];
    const relationField = partitionRelationField(partitionField);
    if (relationField) {
        fields.push(`${relationField} { id name }`);
    }

    if (apiType === "execution") {
        fields.push("status", "type", "message");
    } else if (apiType === "custom_object") {
        fields.push("provisioning_state");
    }

    return fields.join("\n");
};

/** e.g. project_id -> project_id_project for group header labels. */
const partitionRelationField = (field: string): string | null => {
    if (!field.endsWith("_id")) {
        return null;
    }
    const base = field.slice(0, -3);
    return base ? `${field}_${base}` : null;
};

const serializeFilter = (filter: unknown): string | null => {
    if (filter == null) {
        return null;
    }
    return typeof filter === "string" ? filter : JSON.stringify(filter);
};

// Execution status messages are often multi-line ("Line 77:\n\n<text>"). The row
// label renders whitespace as-is, so collapse the message to one line and let the
// label's ellipsis truncate it.
export const displayName = (record: LatestListRecord, apiType: string): string => {
    if (apiType === "execution") {
        return getStatusMessageText(record.message, record.status).replace(/\s+/g, " ").trim();
    }
    return record.name;
};

const groupLabelForRecord = (
    record: LatestListRecord,
    partitionField: string,
    partitionRelation: string | null
): string => {
    if (partitionRelation) {
        const related = record[partitionRelation] as { name?: string } | null | undefined;
        if (related?.name) {
            return related.name;
        }
    }
    const value = record[partitionField];
    if (value == null || value === "") {
        return "(none)";
    }
    return String(value);
};

/**
 * Partition API order is by partition key; re-sort groups client-side using the
 * widget-level `order` (within-partition order uses `partition_by.order`).
 */
const sortGroupsByOrder = (
    groups: GroupWithMembers<LatestListRecord>[],
    order: string
): GroupWithMembers<LatestListRecord>[] => {
    const desc = order.startsWith("-");
    const field = order.replace(/^[+-]/, "");

    if (field === "name") {
        return [...groups].sort((a, b) => {
            const cmp = a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
            return desc ? -cmp : cmp;
        });
    }

    if (field === "created_at") {
        return [...groups].sort((a, b) => {
            const latest = (items: LatestListRecord[]) =>
                Math.max(...items.map((record) => (record.created_at ? Date.parse(record.created_at) : 0)));
            const delta = latest(b.members) - latest(a.members);
            return desc ? delta : -delta;
        });
    }

    return groups;
};

/**
 * `latest_n_list` widget — the last N records per distinct group value.
 *
 * Uses the list API's `partition_by` parameter (window rank per field). The rows
 * render in the shared RecordTable with row grouping (`options.groupBy`): one
 * collapsible header per group with a member count, and the same rich row layout
 * as project/bundle "Latest executions" widgets (status badges, type icons,
 * relative timestamp).
 * (operator-task-fe-recordtable-row-grouping-support-and-latestn)
 */
export const LatestNListWidget = ({
    config,
    widget,
}: WidgetComponentProps<LatestNListWidgetConfig>) => {
    const [records, setRecords] = useState<LatestListRecord[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState(true);
    const isMounted = useRef(true);

    const recordType = config?.record_type;
    const apiType = typeof recordType === "string" ? recordType.toLowerCase() : "";
    const partitionField = config?.partition_by?.field ?? "";
    const partitionRelation = partitionField ? partitionRelationField(partitionField) : null;
    const defaultOrder = config?.order ?? DEFAULT_ORDER;
    const partitionBy = useMemo(() => {
        if (!partitionField) {
            return null;
        }
        return {
            field: partitionField,
            limit: config?.partition_by?.limit ?? config?.n ?? DEFAULT_N_PER_GROUP,
            order: config?.partition_by?.order ?? defaultOrder,
        };
    }, [config?.n, config?.partition_by, defaultOrder, partitionField]);
    const groupLimit = config?.limit ?? DEFAULT_GROUP_LIMIT;
    const filter = serializeFilter(config?.filter);
    const live = config?.live ?? false;

    const fetchRecords = useCallback(async (): Promise<boolean> => {
        if (!apiType) {
            if (isMounted.current) {
                setError("Widget config is missing a record_type.");
                setLoading(false);
            }
            return false;
        }
        if (!partitionBy) {
            if (isMounted.current) {
                setError("Widget config is missing partition_by.field.");
                setLoading(false);
            }
            return false;
        }

        const listField = `${apiType}_plain_list`;
        const query = `query DashboardLatestNList(
            $limit: Int,
            $order: String,
            $filter: String,
            $partitionBy: PartitionByInput
        ) {
            ${listField}(
                limit: $limit,
                order: $order,
                filter: $filter,
                partition_by: $partitionBy
            ) {
                id
                name
                created_at
                ${extraFieldsForType(apiType, partitionField)}
            }
        }`;

        try {
            const response = await graphqlPost({
                query,
                variables: {
                    limit: groupLimit,
                    order: defaultOrder,
                    filter,
                    partitionBy,
                },
            });
            const data = response?.data?.[listField];
            if (isMounted.current) {
                setRecords(Array.isArray(data) ? data : []);
                setError(null);
                setLoading(false);
            }
            return true;
        } catch (err: any) {
            if (isMounted.current) {
                setError(describeWidgetFetchError(err, "Failed to load records."));
                setLoading(false);
            }
            return false;
        }
    }, [apiType, defaultOrder, filter, groupLimit, partitionBy, partitionField]);

    const { lastUpdatedAt, refresh, isRefreshing } = useDashboardLiveRefresh({
        live,
        pollIntervalMs: POLL_INTERVAL_MS,
        fetch: fetchRecords,
    });

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        setLoading(true);
    }, [fetchRecords]);

    const linkBaseType = recordType ?? apiType;

    const groupBy = useMemo(
        () => ({
            getGroupKey: (record: LatestListRecord) => String(record[partitionField] ?? ""),
            getGroupLabel: (record: LatestListRecord) =>
                groupLabelForRecord(record, partitionField, partitionRelation),
            sortGroups: (groups: GroupWithMembers<LatestListRecord>[]) =>
                sortGroupsByOrder(groups, defaultOrder),
        }),
        [partitionField, partitionRelation, defaultOrder]
    );

    const columns = useMemo<TableColumn[]>(
        () => [
            {
                label: "Name",
                key: "name",
                shown: true,
                // The widget is narrow: Name takes the width left after Created and
                // truncates, so a long execution message cannot push Created and
                // the group counts out of view. overflowWrap lets a long unbroken
                // token (path, version string) break instead of widening the column.
                cellStyle: {
                    style: { width: "100%", maxWidth: 0, minWidth: "120px", overflowWrap: "anywhere" },
                },
                dto: (record: LatestListRecord) => (
                    <DashboardRecordLink
                        id={record.id}
                        name={record.name}
                        recordType={linkBaseType}
                        displayName={displayName(record, apiType)}
                        provisioningState={record.provisioning_state}
                        icon={
                            apiType === "execution" && record.type
                                ? getByExecutionTypeName(
                                      record.type.toLowerCase() as FeExecutionType
                                  )
                                : undefined
                        }
                    />
                ),
            },
            {
                label: "Created",
                key: "created_at",
                shown: true,
                cellStyle: { style: { whiteSpace: "nowrap" } },
                dto: (record: LatestListRecord) =>
                    record.created_at ? <TimeAgo datetime={record.created_at} /> : null,
            },
        ],
        [apiType, linkBaseType]
    );

    const rows = useMemo(() => records.filter((record) => record?.id), [records]);

    let content;
    // Keep the last rows on a failed re-fetch; surface the error inline on the
    // live bar instead of blanking.
    if (error && records.length === 0) {
        content = <div className="DashboardWidget__error">{error}</div>;
    } else if (isLoading) {
        content = <div className="DashboardWidget__empty">Loading…</div>;
    } else if (rows.length === 0) {
        content = <div className="DashboardWidget__empty">No records.</div>;
    } else {
        content = (
            <RecordTable
                className="DashboardLatestNList"
                data={rows}
                totalItemCount={rows.length}
                columns={columns}
                recordTypes={null}
                isLoading={false}
                refresh={refresh}
                tableSettingsKey={`dashboard-widget-${widget?.uid ?? "latest-n-list"}`}
                options={{
                    selectableRows: "none",
                    toolbar: "controls",
                    density: "high",
                    showSearch: false,
                    groupBy,
                }}
            />
        );
    }

    return (
        <DashboardLiveWidgetShell
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            error={error}
        >
            {content}
        </DashboardLiveWidgetShell>
    );
};
